import type { ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useUserOrg } from '../lib/org';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { BottomTabBar } from './BottomTabBar';
import { isInSetupMode, exitSetupMode } from '../data/siteSetupChecklist';
import { Logo } from './Logo';
import { useConfirm } from './ConfirmProvider';

interface Props {
  title?: string;
  /**
   * Optional override for `document.title` only. Useful when you want the
   * browser-tab title to include extra context (e.g. the active site name)
   * that wouldn't fit on the visible header. Defaults to `title`.
   */
  documentTitle?: string;
  back?: string;        // fallback path used if there's no in-app history
  topRight?: ReactNode; // overrides the default right-hand slot
  children: ReactNode;
}

export function AppShell({ title, documentTitle, back, topRight, children }: Props) {
  const { org } = useUserOrg();
  const orgName = org?.org_name ?? '';
  const nav = useNavigate();
  const loc = useLocation();
  const { confirm } = useConfirm();

  // Browser-tab title — uses the override if provided, otherwise the visible
  // header title. Pages that need extra context in the tab (active site name,
  // record id, etc.) should pass `documentTitle` explicitly.
  useDocumentTitle(documentTitle ?? title);

  function handleBack() {
    if (loc.key !== 'default') {
      nav(-1);
      return;
    }
    nav(back ?? '/');
  }

  const setupSiteId = isInSetupMode();
  const onSetupPage = setupSiteId && loc.pathname === `/sites/${setupSiteId}/setup`;

  async function exitSetup() {
    const ok = await confirm({
      title: 'Exit site setup?',
      body: 'You can come back later from the active site banner.',
      confirmLabel: 'Exit setup',
    });
    if (ok) {
      exitSetupMode();
      nav('/');
    }
  }

  const right = topRight ?? (
    setupSiteId ? (
      <button type="button" className="settings-link" onClick={exitSetup}>Exit setup</button>
    ) : back ? (
      <span className="org-name">{orgName}</span>
    ) : (
      <Link to="/settings" className="settings-link">Settings</Link>
    )
  );

  return (
    <div className="app-container">
      {/* Skip-to-content link — visually hidden until keyboard-focused, at
          which point it pops in over the top-left corner. Lets keyboard
          users and AI agents jump straight to <main> instead of tabbing
          through the back button, page title, settings link, and tab bar
          first. The href targets the id below on <main>. */}
      <a href="#main" className="skip-to-main">Skip to main content</a>

      {setupSiteId && !onSetupPage && (
        <Link to={`/sites/${setupSiteId}/setup`} className="setup-mode-banner">
          ← Back to site setup
        </Link>
      )}
      <header className="app-header">
        <div className="app-header-left">
          {back ? (
            <button type="button"
              onClick={() => {
                if (setupSiteId && !onSetupPage) {
                  nav(`/sites/${setupSiteId}/setup`);
                  return;
                }
                handleBack();
              }}
              className="back-link" aria-label="Back">
              ‹ {setupSiteId && !onSetupPage ? 'Back to setup' : 'Back'}
            </button>
          ) : title ? (
            <span className="page-title-header">{title}</span>
          ) : (
            <span className="brand-small">
              <Logo size={26} decorative />
              <span className="brand-small-text">{orgName || 'Safe and Sorted'}</span>
            </span>
          )}
        </div>
        <div className="app-header-center">
          {back && title && <span className="page-title-header">{title}</span>}
        </div>
        <div className="app-header-right">{right}</div>
      </header>

      <main id="main" className="app-main" tabIndex={-1}>{children}</main>

      {!setupSiteId && <BottomTabBar />}
    </div>
  );
}
